<template>
    <div class="status-control-wrapper">
        <v-select 
            :items="items"
            v-model="internalSelected"
            :hint="`${selected.name}`"
            density="compact"
            label="Status"
            min-width="150"
            item-title="name"
            item-value="id"
            return-object
            single-line
            hide-details="true"
            @update:modelValue="onChange"
            :bg-color="bgcolor"
            >
        </v-select>
        <v-btn
            v-if="showSaveButton"
            color="primary"
            class="ml-2"
            icon
            size="x-small"
            variant="tonal"
            :disabled="saving"
            @click="saveSelection"
        >
            <v-icon>mdi-content-save</v-icon>
        </v-btn>
        <span v-if="saving" class="status-saving">
            <v-icon size="14" class="spin">mdi-loading</v-icon>
            Guardando...
        </span>
        <span v-else-if="saved" class="status-saved">
            <v-icon size="14">mdi-check-circle</v-icon>
            Guardado
        </span>
    </div>
</template>

<script setup>
    import { onMounted, ref, watch } from 'vue'

    const props = defineProps({
        items: {
            type: Array,
            required: true
        },
        selected: {
            type: Object,
            default: () => ({ id: 2, name: 'Pendiente' })
        },
        selectedItem: {
            type: Object,
            default: null
        },
        saving: {
            type: Boolean,
            default: false
        },
        saved: {
            type: Boolean,
            default: false
        },
        autoSave: {
            type: Boolean,
            default: true
        },
        showSaveButton: {
            type: Boolean,
            default: true
        }
    })

    // Variable interna para v-model
    const internalSelected = ref(props.selected);

    const bgcolor = ref('#cccccc');

    // (Opcional) Emitir cambios hacia el padre si es necesario
    const emit = defineEmits(['update:selected', 'save:selected']);

    const getBgColor = () => {
        let name = props.items.find(item => item.id === internalSelected.value?.id)?.name;
        if (name === 'Pendiente') {
            bgcolor.value = '#cccccc';
        } else if (name === 'Aprobado') {
            bgcolor.value = '#77e1a8db';
        } else if ( name === 'Denegado' ) {
            bgcolor.value = '#e22454bf';
        }
    }

    function syncSelectedFromItems() {
        if (props.items.length > 0 && props.selectedItem) {
            const match = props.items.find(item => item.id === props.selectedItem.status_id);
            if (match) {
                internalSelected.value = match;
            }
        }
        getBgColor();
    }

    function onChange(newValue) {
        internalSelected.value = newValue
        if (props.autoSave) {
            emit('update:selected', newValue) // Esto llama al padre
        }
        getBgColor();
    }

    function saveSelection() {
        if (!internalSelected.value) return
        emit('save:selected', internalSelected.value)
    }

    // items y selectedItem pueden llegar de forma asíncrona y en distinto orden
    // (p.ej. edit_product_comment.vue los resuelve en dos peticiones separadas);
    // hay que resincronizar cuando cualquiera de los dos cambie, sin pisar
    // internalSelected con undefined si todavía no hay match.
    watch(() => props.items, syncSelectedFromItems, { immediate: true });
    watch(() => props.selectedItem, syncSelectedFromItems);

    onMounted(() => {
        // Inicializar el color de fondo al montar el componente
        getBgColor();
    })
</script>

<style scoped>
.status-control-wrapper {
    display: flex;
    align-items: center;
    gap: 6px;
}

.status-saving {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: #1565c0;
    font-size: 12px;
}

.status-saved {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: #2e7d32;
    font-size: 12px;
}

.spin {
    animation: status-spin 0.9s linear infinite;
}

@keyframes status-spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}
</style>